import { create } from 'zustand';
import { adminUserApi } from '@api/api-config.ts';
import { AdminUserListResponse } from '@api/generated/portal-be-admin';

interface DeleteDialogState {
  visible: boolean;
  userId: number | null;
  userName: string;
}

interface AdminUserFilters {
  q?: string;
  organizationId?: number;
  page?: number;
  size?: number;
}

interface AdminUserStoreState {
  loading: boolean;
  error: string | null;

  data: AdminUserListResponse[] | null;

  total: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrev: boolean;

  deleteDialog: DeleteDialogState;
  isDeleting: boolean;

  loadUsers: (filters?: AdminUserFilters) => Promise<void>;
  handleDeleteClick: (user: AdminUserListResponse) => void;
  handleDeleteConfirm: () => Promise<void>;
  handleDeleteCancel: () => void;
}

export const useCreateAdminUserStore = create<AdminUserStoreState>((set, get) => ({
  loading: false,
  error: null,

  data: null,

  total: 0,
  totalPages: 0,
  currentPage: 1,
  pageSize: 20,
  hasNext: false,
  hasPrev: false,

  deleteDialog: {
    visible: false,
    userId: null,
    userName: '',
  },
  isDeleting: false,

  loadUsers: async (filters?: AdminUserFilters) => {
    try {
      set({ loading: true, error: null });

      const page = filters?.page || 1;
      const size = filters?.size || 20;

      const response = await adminUserApi.getUsers({
        // AdminUserReadService jede přes loadPageQuerydsl, tedy 1-based — page-1 vracelo o stránku vedle.
        page: page,
        size: size,
        q: filters?.q,
        organizationId: filters?.organizationId,
      });

      set({
        loading: false,
        error: null,
        data: response.data.items,
        total: response.data.total || 0,
        totalPages: response.data.totalPages || 0,
        currentPage: page,
        pageSize: size,
        hasNext: page < (response.data.totalPages || 0),
        hasPrev: page > 1,
      });
    } catch (error) {
      set({ loading: false, error: error instanceof Error ? error.message : 'Chyba při načítání' });
    }
  },

  handleDeleteClick: (user: AdminUserListResponse) => {
    const userName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Uživatel';
    set({
      deleteDialog: {
        visible: true,
        userId: user.id,
        userName,
      },
    });
  },

  handleDeleteConfirm: async () => {
    const { deleteDialog } = get();
    if (!deleteDialog.userId) return;

    set({ isDeleting: true });
    try {
      await adminUserApi.deleteUser({ userId: deleteDialog.userId });
      set({
        deleteDialog: { visible: false, userId: null, userName: '' },
        isDeleting: false,
      });
      // Refresh will be triggered by URL change in the component
    } catch (error) {
      console.error('Chyba při mazání uživatele:', error);
      set({ isDeleting: false });
    }
  },

  handleDeleteCancel: () => {
    set({
      deleteDialog: { visible: false, userId: null, userName: '' },
    });
  },
}));
